import { useState, FormEvent, useEffect } from 'react';
import { useRouter } from 'next/router';
import * as Sentry from '@sentry/node';
import Layout from 'components/Layout';
import Modal from 'components/Modal';
import api from 'utils/api';
import { track } from 'utils/api';
import {
  getLocalOrganization,
  saveOrganizationToLocalStorage,
} from 'utils/userDetails';

const NewLounge = () => {
  const [name, setName] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [error, setError] = useState({ hidden: true, heading: '', body: '' });
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const { plan } = router.query;

    // if there is a local org and the plan param has been set, then
    // take them to the settings page directly
    if (getLocalOrganization() && plan) {
      router.push({
        pathname: '/settings',
        query: {
          plan: plan,
        },
      });
    }
  }, []);

  async function createNewOrg() {
    setIsLoading(true);
    try {
      const res = await api.post('/api/organizations', {
        name: name,
        email: email,
      });

      // track the new org
      track(
        'new_organization_created',
        {
          query_params: router.query,
        },
        res.data.id,
      );

      setIsLoading(false);
      saveOrganizationToLocalStorage(res.data);

      // if a plan param has been set then forcefully take them to the settings page
      const { plan } = router.query;
      if (plan && (plan as string) != 'free') {
        router.push({
          pathname: `/lounge/${res.data.slug}/billing`,
          query: {
            plan: plan,
          },
        });
      } else {
        router.push(`/lounge/${res.data.slug}`);
      }
    } catch (e) {
      Sentry.captureException(e);
      setIsLoading(false);
      setError({
        hidden: false,
        heading: 'Error creating organization!',
        body:
          'There was a problem creating the organization, please try again!',
      });
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    await createNewOrg();
  }

  function hideError() {
    setError({ hidden: true, heading: '', body: '' });
  }

  return (
    <Layout title="Create new Lounge">
      <Modal
        {...error}
        showConfirmButton={true}
        confirmLabel="Ok"
        modalType={'info'}
        onConfirm={hideError}
      />

      <div>
        <h2 className="mt-6 text-center text-3xl leading-9 font-extrabold text-gray-900">
          Create a Lounge for your team!
        </h2>
      </div>

      <form className="mt-8" onSubmit={handleSubmit}>
        <div>
          <div className="mt-6">
            <label
              className="block text-sm leading-5 font-medium text-gray-700"
              htmlFor="name"
            >
              Organization
            </label>
            <input
              aria-label="Organization name"
              id="name"
              name="name"
              type="text"
              required
              className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 sm:text-sm sm:leading-5"
              placeholder="Organization Name"
              value={name ? name : ''}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="mt-6">
            <label
              className="block text-sm leading-5 font-medium text-gray-700"
              htmlFor="name"
            >
              Email
            </label>
            <input
              aria-label="Email"
              id="email"
              name="email"
              type="email"
              required
              className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 sm:text-sm sm:leading-5"
              placeholder="you@company.com"
              value={email ? email : ''}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="mt-6 flex items-center">
            <input
              id="agree_to_privacy"
              type="checkbox"
              className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out"
              required
            />
            <label
              htmlFor="agree_to_privacy"
              className="ml-2 block text-sm leading-5 text-gray-900"
            >
              I agree to the{' '}
              <a
                className="text-indigo-600 font-medium"
                href="https://getlounge.app/privacy-policy"
              >
                Privacy Policy
              </a>
            </label>
          </div>
        </div>

        <div className="mt-6">
          <button
            type="submit"
            className="btn btn-indigo w-full leading-5"
            disabled={isLoading}
          >
            {isLoading ? 'Creating new lounge area...' : 'Create your Lounge'}
          </button>
        </div>
      </form>
    </Layout>
  );
};

export default NewLounge;
